import { Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Calendar,
  Clock,
  GraduationCap,
  Award,
  Stethoscope,
  ArrowLeft,
  Phone,
  MapPin,
  CheckCircle2,
  ShieldCheck,
  Building2,
  ChevronRight,
  Info,
  Users,
  Languages,
  BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/knmc/Header";
import { Footer } from "@/components/knmc/Footer";
import { EmergencyBar } from "@/components/knmc/EmergencyBar";
import { Reveal } from "@/components/knmc/Reveal";
import type { DoctorDetail as DoctorDetailType } from "@/hooks/useDoctors";
import { useContactSettings } from "@/hooks/useContactSettings";
import { buildUpcomingSchedule, type ScheduleDay } from "@/lib/schedule";
import { toList } from "@/lib/jsonld";
import docFemaleImage from "@/assets/Doctor Portrait Avatar (1).jpg";
import docMaleImage from "@/assets/Doctor Portrait Avatar.jpg";

// Renders text that could be an HTML string from a rich text editor, or multi-line plain text
function RichOrPlainText({ content }: { content: string }) {
  const isHtml = /<[a-z][\s\S]*>/i.test(content);
  if (isHtml) {
    return (
      <div
        className="prose prose-sm max-w-none text-stone-600 leading-relaxed"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }
  return (
    <p className="whitespace-pre-line text-sm text-stone-600 leading-relaxed font-normal">
      {content}
    </p>
  );
}

function SectionCard({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-4 shadow-sm sm:p-6">
      <h2 className="mb-3 flex items-center gap-2 font-serif text-base font-bold text-stone-900 sm:text-lg">
        <Icon aria-hidden className="h-4 w-4 text-[#04332B]" /> {title}
      </h2>
      {children}
    </div>
  );
}

export default function DoctorDetail({ doctor }: { doctor: DoctorDetailType }) {
  const navigate = useNavigate();
  const { settings: contactSettings } = useContactSettings();

  // Depends on "today" in the visitor's timezone, which can be a different date
  // from the server's, so it's built after mount to keep hydration consistent.
  const [schedule, setSchedule] = useState<ScheduleDay[]>([]);
  useEffect(() => {
    setSchedule(doctor ? buildUpcomingSchedule(doctor.availability) : []);
  }, [doctor]);

  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"about" | "schedule">("about");

  const placeholderImage = doctor?.gender === "Female" ? docFemaleImage : docMaleImage;
  const [imgSrc, setImgSrc] = useState<string>(doctor?.image || placeholderImage);

  const dateRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const slotRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // --- Dynamic Meta Title & Description ---
  useEffect(() => {
    if (!doctor) return;
    const defaultTitle = `${doctor.practitioner_name} - ${doctor.department} | Kathmandu National Hospital`;
    const defaultDesc = `Consult with ${doctor.practitioner_name}, ${doctor.department} specialist at Kathmandu National Hospital. Experience: ${doctor.experience || "N/A"}.`;

    document.title = doctor.meta_title || defaultTitle;

    // 1. Meta Description
    let metaDescTag = document.querySelector('meta[name="description"]');
    if (!metaDescTag) {
      metaDescTag = document.createElement("meta");
      metaDescTag.setAttribute("name", "description");
      document.head.appendChild(metaDescTag);
    }
    metaDescTag.setAttribute("content", doctor.meta_description || defaultDesc);
  }, [doctor]);

  useEffect(() => {
    setImgSrc(doctor?.image || placeholderImage);
  }, [doctor?.image, placeholderImage]);

  useEffect(() => {
    if (!selectedDate && schedule.length) {
      const firstAvailable = schedule.find((d) => d.slots.length) ?? schedule[0];
      setSelectedDate(firstAvailable.iso);
    }
  }, [schedule, selectedDate]);

  const handleRovingKeyDown = useCallback(
    (
      e: React.KeyboardEvent,
      refs: React.MutableRefObject<Array<HTMLButtonElement | null>>,
      enabledIndexes: number[],
      currentIndex: number,
    ) => {
      if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(e.key)) return;
      e.preventDefault();
      const pos = enabledIndexes.indexOf(currentIndex);
      let nextPos = pos;
      if (e.key === "ArrowRight") nextPos = (pos + 1) % enabledIndexes.length;
      if (e.key === "ArrowLeft") nextPos = (pos - 1 + enabledIndexes.length) % enabledIndexes.length;
      if (e.key === "Home") nextPos = 0;
      if (e.key === "End") nextPos = enabledIndexes.length - 1;
      const nextIndex = enabledIndexes[nextPos];
      refs.current[nextIndex]?.focus();
      refs.current[nextIndex]?.click();
    },
    [],
  );

  // Normalized values
  const specialtiesList = useMemo(() => toList(doctor?.specialties), [doctor?.specialties]);
  const qualificationsList = useMemo(() => toList(doctor?.qualifications), [doctor?.qualifications]);
  const languagesList = useMemo(() => toList(doctor?.languages), [doctor?.languages]);

  const awardsList = useMemo(() => {
    if (!doctor?.awards) return [];
    return doctor.awards
      .map((item) => (typeof item === "string" ? item : item.award))
      .filter(Boolean);
  }, [doctor?.awards]);

  if (!doctor) return null;

  const currentDay = schedule.find((d) => d.iso === selectedDate);
  const enabledDateIndexes = schedule
    .map((d, i) => (d.slots.length ? i : -1))
    .filter((i) => i !== -1);
  const enabledSlotIndexes = (currentDay?.slots ?? []).map((_, i) => i);

  return (
    <main className="min-h-screen w-full overflow-x-hidden bg-[#F8F9FA] text-stone-900 antialiased">
      <Header />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="border-b border-stone-200/80 bg-white">
        <div className="mx-auto flex max-w-7xl min-w-0 items-center gap-2 overflow-x-auto whitespace-nowrap px-4 py-3 text-xs font-medium text-stone-500 sm:px-6">
          <Link
            to="/"
            className="rounded-sm hover:text-[#04332B] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#04332B]"
          >
            Home
          </Link>
          <ChevronRight aria-hidden className="h-3 w-3 text-stone-400" />
          <Link
            to="/doctors"
            className="rounded-sm hover:text-[#04332B] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#04332B]"
          >
            Consultants & Surgeons
          </Link>
          <ChevronRight aria-hidden className="h-3 w-3 text-stone-400" />
          <span aria-current="page" className="min-w-0 max-w-[55vw] truncate font-semibold text-stone-900">
            {doctor.practitioner_name}
          </span>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section
        aria-labelledby="doctor-name-heading"
        className="relative overflow-hidden bg-[#04332B] pt-5 pb-8 text-white sm:pt-6 sm:pb-10 md:pt-8 md:pb-12"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-20 -top-20 h-96 w-96 rounded-full bg-[#D2A662]/10 blur-3xl"
        />

        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <div className="grid min-w-0 gap-6 sm:gap-8 lg:grid-cols-12 lg:items-stretch">
            
            {/* LEFT: Portrait Column */}
            <div className="min-w-0 lg:col-span-5 flex flex-col justify-end">
              <div className="relative mx-auto aspect-[4/5] w-full max-w-[340px] overflow-hidden rounded-3xl border border-[#D2A662]/30 bg-stone-900 shadow-xl sm:aspect-[3/4] sm:max-w-sm lg:max-w-none">
                <img
                  src={imgSrc}
                  alt={`Portrait of ${doctor.practitioner_name}`}
                  width={1024}
                  height={1280}
                  onError={() => setImgSrc(placeholderImage)}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            {/* RIGHT: Info & Booking Column */}
            <div className="min-w-0 lg:col-span-7 flex flex-col justify-between space-y-4">
              <div>
                <Link
                  to="/doctors"
                  className="mb-3 inline-flex max-w-full items-center gap-1.5 rounded-sm text-xs font-semibold uppercase tracking-wider text-emerald-200/90 transition-colors hover:text-[#D2A662]"
                >
                  <ArrowLeft aria-hidden className="h-3.5 w-3.5" /> Back to all doctors
                </Link>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-[#D2A662]/30 bg-[#D2A662]/20 px-3 py-0.5 text-xs font-bold uppercase tracking-wider text-[#D2A662]">
                    <Stethoscope aria-hidden className="h-3.5 w-3.5" />
                    {doctor.department}
                  </span>
                </div>

                <h1
                  id="doctor-name-heading"
                  className="mt-2 break-words font-serif text-2xl font-bold leading-tight tracking-tight text-white sm:text-3xl md:text-4xl"
                >
                  {doctor.practitioner_name.startsWith("Dr.") ? doctor.practitioner_name : `Dr. ${doctor.practitioner_name}`}
                </h1>
                <p className="mt-1 break-words text-sm font-medium text-emerald-100/90">
                  {doctor.role || doctor.department}
                </p>
              </div>

              {/* BOOKING CARD */}
              <div
                id="book"
                className="w-full min-w-0 overflow-hidden rounded-2xl border border-stone-200 bg-white p-4 text-stone-900 shadow-xl sm:p-5"
              >
                <div className="flex flex-col gap-2 border-b border-stone-100 pb-2.5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#04332B]">
                    <Calendar aria-hidden className="h-3.5 w-3.5 text-[#D2A662]" />
                    OPD Availability & Live Booking
                  </div>
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-800">
                    <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Live Schedule
                  </span>
                </div>

                <div className="mt-3 space-y-3 sm:mt-4 sm:space-y-3.5">
                  <fieldset className="w-full min-w-0">
                    <legend className="text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1.5">
                      1. Select Consultation Date
                    </legend>
                    <div
                      role="radiogroup"
                      aria-label="Consultation date"
                      className="flex w-full min-w-0 gap-2 overflow-x-auto pb-1.5 pt-0.5 scrollbar-thin scrollbar-thumb-stone-300"
                    >
                      {schedule.map((d, i) => {
                        const disabled = d.slots.length === 0;
                        const active = d.iso === selectedDate;
                        return (
                          <button
                            key={d.iso}
                            ref={(el) => {
                              dateRefs.current[i] = el;
                            }}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            aria-label={`${d.label} ${d.dayNumber} ${d.monthShort}${disabled ? ", no slots" : ""}`}
                            tabIndex={active ? 0 : -1}
                            disabled={disabled}
                            onKeyDown={(e) => handleRovingKeyDown(e, dateRefs, enabledDateIndexes, i)}
                            onClick={() => {
                              setSelectedDate(d.iso);
                              setSelectedSlot(null);
                            }}
                            className={`flex shrink-0 flex-col items-center rounded-xl border px-3 py-1.5 text-xs font-medium transition-all ${
                              disabled
                                ? "cursor-not-allowed border-stone-200 bg-stone-100 text-stone-400 opacity-60"
                                : active
                                  ? "border-[#04332B] bg-[#04332B] text-white shadow-sm scale-102"
                                  : "border-stone-200 bg-white hover:border-[#04332B] text-stone-700"
                            }`}
                          >
                            <span className="opacity-70 text-[9px] uppercase">{d.label}</span>
                            <span className="text-xs font-bold">{d.dayNumber}</span>
                            <span className="opacity-70 text-[9px]">{d.monthShort}</span>
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>

                  <fieldset className="w-full min-w-0">
                    <legend className="text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1.5">
                      2. Available Time Windows
                    </legend>
                    <div className="rounded-xl border border-stone-200 bg-stone-50 p-2 min-h-[90px]">
                      {currentDay?.slots.length ? (
                        <div
                          role="radiogroup"
                          aria-label="Consultation time slot"
                          className="grid grid-cols-2 gap-1.5 overflow-y-auto pr-1 sm:grid-cols-3 max-h-32"
                        >
                          {currentDay.slots.map((s, i) => {
                            const isSelected = selectedSlot === s;
                            const startTime = s.split("-")[0].trim();
                            const [hh, mm] = startTime.split(":");
                            const formattedTime = `${hh}:${mm}`;
                            return (
                              <button
                                key={s}
                                ref={(el) => {
                                  slotRefs.current[i] = el;
                                }}
                                type="button"
                                role="radio"
                                aria-checked={isSelected}
                                aria-label={`${formattedTime} time slot`}
                                tabIndex={isSelected || (!selectedSlot && i === 0) ? 0 : -1}
                                onKeyDown={(e) => handleRovingKeyDown(e, slotRefs, enabledSlotIndexes, i)}
                                onClick={() => setSelectedSlot(s)}
                                className={`rounded-lg py-1.5 px-2 text-center text-xs font-bold transition-all ${
                                  isSelected
                                    ? "bg-[#04332B] text-white shadow-sm ring-2 ring-emerald-600"
                                    : "bg-white text-stone-700 border border-stone-200 hover:border-[#04332B]"
                                }`}
                              >
                                {formattedTime}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="flex h-16 items-center justify-center text-center">
                          <p className="text-xs font-medium text-stone-400">
                            No OPD slots available on this date
                          </p>
                        </div>
                      )}
                    </div>
                  </fieldset>

                  <Button
                    onClick={() => {
                      if (selectedSlot && selectedDate) {
                        navigate({
                          to: "/book/$name",
                          params: { name: doctor.name },
                          search: { date: selectedDate, slot: selectedSlot },
                        });
                      }
                    }}
                    className="w-full rounded-full bg-[#04332B] px-3 py-3.5 text-[11px] font-bold uppercase tracking-wide text-white shadow-md transition-all hover:bg-[#06483d] disabled:opacity-50 sm:py-4 sm:text-xs sm:tracking-wider"
                    disabled={!selectedSlot}
                  >
                    {selectedSlot ? `Confirm ${selectedSlot} & Book` : "Select a time slot above"}
                  </Button>
                </div>
              </div>

              <a
                href={`tel:${contactSettings?.phone || ""}`}
                className="inline-flex w-full min-w-0 flex-wrap items-center justify-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-2.5 text-center text-xs font-semibold text-white transition-all hover:border-[#D2A662] hover:bg-white/20 sm:px-4"
              >
                <Phone aria-hidden className="h-3.5 w-3.5 text-[#D2A662]" />
                Direct OPD Hospital Call: {contactSettings?.phone || "01-4771556"}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CREDENTIAL STRIP */}
      <section aria-label="Key credentials" className="border-b border-stone-200/80 bg-white py-4 sm:py-5">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <div className="grid grid-cols-2 gap-2.5 sm:gap-3 md:grid-cols-4">
            {[
              { icon: Award, label: "Experience", value: doctor.experience ? `${doctor.experience} Years` : "Not specified" },
              {
                icon: ShieldCheck,
                label: "NMC Status",
                value: doctor.nmc_number ? `NMC: ${doctor.nmc_number}` : "Verified Doctor",
              },
              { icon: Clock, label: "OPD Schedule", value: "Sunday – Friday" },
              { icon: MapPin, label: "Department", value: doctor.department },
            ].map(({ icon: Icon, label, value }) => (
              <div
                key={label}
                className="flex min-w-0 items-start gap-2.5 rounded-2xl border border-stone-200/80 bg-stone-50 p-3 sm:items-center sm:gap-3 sm:p-3.5"
              >
                <Icon aria-hidden className="h-4 w-4 text-[#04332B] shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">
                    {label}
                  </div>
                  <div className="break-words font-bold text-xs text-stone-900 sm:text-sm">{value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOWER CONTENT SECTION */}
      <section className="py-8 sm:py-12">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <div className="grid min-w-0 gap-8 lg:grid-cols-12 lg:gap-10">
            
            <div className="min-w-0 lg:col-span-8 space-y-6 sm:space-y-8">
              <div role="tablist" aria-label="Doctor profile sections" className="flex max-w-full gap-6 overflow-x-auto border-b border-stone-200 sm:gap-8">
                {(
                  [
                    { id: "about", label: "Doctor Profile" },
                    { id: "schedule", label: "Full Weekly OPD Schedule" },
                  ] as const
                ).map((tab, i) => (
                  <button
                    key={tab.id}
                    ref={(el) => {
                      tabRefs.current[i] = el;
                    }}
                    id={`tab-${tab.id}`}
                    role="tab"
                    type="button"
                    aria-selected={activeTab === tab.id}
                    aria-controls={`panel-${tab.id}`}
                    tabIndex={activeTab === tab.id ? 0 : -1}
                    onClick={() => setActiveTab(tab.id)}
                    className={`shrink-0 whitespace-nowrap pb-3 text-xs font-bold transition-all rounded-t-sm sm:text-sm ${
                      activeTab === tab.id
                        ? "text-[#04332B] border-b-2 border-[#04332B]"
                        : "text-stone-500 hover:text-stone-700"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* TAB PANEL: Profile */}
              <div
                id="panel-about"
                role="tabpanel"
                aria-labelledby="tab-about"
                hidden={activeTab !== "about"}
                tabIndex={0}
                className="space-y-6"
              >
                {doctor.bio && (
                  <Reveal>
                    <SectionCard title="Professional Biography" icon={Info}>
                      <RichOrPlainText content={doctor.bio} />
                    </SectionCard>
                  </Reveal>
                )}

                {specialtiesList.length > 0 && (
                  <Reveal>
                    <SectionCard title="Area of Focus" icon={Stethoscope}>
                      <div className="flex flex-wrap gap-2">
                        {specialtiesList.map((s) => (
                          <span
                            key={s}
                            className="rounded-full border border-[#04332B]/15 bg-emerald-50/70 px-3.5 py-1 text-xs font-semibold text-[#04332B]"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </SectionCard>
                  </Reveal>
                )}

                {(Boolean(doctor.education?.length) || qualificationsList.length > 0) && (
                  <Reveal>
                    <SectionCard title="Education & Qualifications" icon={GraduationCap}>
                      <ul className="space-y-3">
                        {doctor.education?.map((ed, idx) => (
                          <li key={idx} className="flex items-start gap-2.5 text-xs text-stone-800 font-medium">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#04332B]" />
                            <div>
                              <span className="font-bold text-stone-900">{ed.year}</span> — {ed.detail}
                            </div>
                          </li>
                        ))}
                        {qualificationsList.map((q) => (
                          <li key={q} className="flex items-start gap-2.5 text-xs text-stone-800 font-medium">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#04332B]" />
                            <span>{q}</span>
                          </li>
                        ))}
                      </ul>
                    </SectionCard>
                  </Reveal>
                )}

                {doctor.custom_fellowship__training && (
                  <Reveal>
                    <SectionCard title="Fellowship & Training" icon={ShieldCheck}>
                      <RichOrPlainText content={doctor.custom_fellowship__training} />
                    </SectionCard>
                  </Reveal>
                )}

                {doctor.experience && (
                  <Reveal>
                    <SectionCard title="Experience" icon={Clock}>
                      <p className="text-sm text-stone-700 leading-relaxed font-medium">
                        {doctor.experience.includes("Year")
                          ? doctor.experience
                          : `${doctor.experience} Years of Clinical Experience`}
                      </p>
                    </SectionCard>
                  </Reveal>
                )}

                {doctor.custom_memberships && (
                  <Reveal>
                    <SectionCard title="Memberships" icon={Users}>
                      <RichOrPlainText content={doctor.custom_memberships} />
                    </SectionCard>
                  </Reveal>
                )}

                {awardsList.length > 0 && (
                  <Reveal>
                    <SectionCard title="Awards & Honours" icon={Award}>
                      <ul className="space-y-2">
                        {awardsList.map((award, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-xs text-stone-800 font-medium">
                            <Award className="mt-0.5 h-4 w-4 shrink-0 text-[#D2A662]" />
                            <span>{award}</span>
                          </li>
                        ))}
                      </ul>
                    </SectionCard>
                  </Reveal>
                )}

                {doctor.custom_research_and_publications && (
                  <Reveal>
                    <SectionCard title="Research & Publications" icon={BookOpen}>
                      <RichOrPlainText content={doctor.custom_research_and_publications} />
                    </SectionCard>
                  </Reveal>
                )}

                {languagesList.length > 0 && (
                  <Reveal>
                    <SectionCard title="Languages Spoken" icon={Languages}>
                      <div className="flex flex-wrap gap-2">
                        {languagesList.map((lang) => (
                          <span
                            key={lang}
                            className="rounded-lg border border-stone-200 bg-stone-100 px-3 py-1 text-xs font-medium text-stone-800"
                          >
                            {lang}
                          </span>
                        ))}
                      </div>
                    </SectionCard>
                  </Reveal>
                )}
              </div>

              {/* TAB PANEL: Schedule */}
              <div
                id="panel-schedule"
                role="tabpanel"
                aria-labelledby="tab-schedule"
                hidden={activeTab !== "schedule"}
                tabIndex={0}
              >
                {activeTab === "schedule" && (
                  <div className="rounded-3xl border border-stone-200 bg-white p-4 shadow-sm sm:p-6">
                    <h2 className="mb-3 flex items-center gap-2 font-display text-sm font-bold text-stone-900">
                      <Clock aria-hidden className="h-4 w-4 text-[#04332B]" /> Weekly OPD Hours Breakdown
                    </h2>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
                      {doctor.availability.map((d) => (
                        <div
                          key={d.day}
                          className={`rounded-2xl border p-3 text-center ${
                            d.slots.length
                              ? "border-emerald-200 bg-emerald-50/50 text-[#04332B]"
                              : "border-stone-200 bg-stone-100 text-stone-500"
                          }`}
                        >
                          <div className="text-[10px] font-bold uppercase tracking-wider">
                            {d.day.slice(0, 3)}
                          </div>
                          <div className="mt-1 text-xs font-semibold">
                            {d.slots.length ? `${d.slots.length} slots` : "Off Duty"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="min-w-0 lg:col-span-4 space-y-6">
              <Reveal>
                <div className="rounded-3xl border border-stone-200 bg-white p-4 shadow-sm sm:p-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#04332B] text-white">
                      <Building2 aria-hidden className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm text-stone-900">{doctor.department}</h3>
                      <p className="text-xs text-stone-500">Kathmandu National Hospital</p>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-stone-600 leading-relaxed">
                    Equipped with modern diagnostic tools and clinical units to ensure comprehensive patient care.
                  </p>
                  <Link
                    to="/departments"
                    className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#04332B] hover:underline"
                  >
                    Explore department capabilities <ChevronRight aria-hidden className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </Reveal>
            </div>

          </div>
        </div>
      </section>

      <Footer />
      <EmergencyBar />
    </main>
  );
}